import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  CheckCircle as ReadyIcon,
  ThumbUp as ApproveIcon,
  Receipt as CheckIcon,
  History as HistoryIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import DeliverActionIcon from '../icons/DeliverActionIcon';
import { Link as RouterLink } from 'react-router-dom';
import { getClientTabPath } from '../../utils/navigationReturn';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CertificateLink from '../CertificateLink';
import AddCustomItemDialog from '../AddCustomItemDialog';
import ItemAuditLogDialog, { getAuditStatusLabelClientDetail } from '../checks/ItemAuditLogDialog';
import { formatDateTime } from '../../utils/dateFormat';
import { ORDER_STATUSES, ALL_ORDER_STATUSES, DIAMOND_TYPES } from '../../constants/appConstants';
import { TABLE_ROWS_PREF_KEYS, pickRowsPerPageFromPrefs, saveTableRowsPreference } from '../../utils/tablePagePreferences';
import {
  getItemRef,
  getItemLab,
  getItemDetails,
  getCertifiedDiamondSpecsWithWeight,
  getOrderItemDescriptionCell,
  sortOrderItemsByCreatedAt,
  getItemDisplayPrice,
  getItemPricePerCt,
  getItemTypeLabel,
  getItemExportTypeLabel,
  getItemCarat,
  getOrderId,
  getItemStatusColorClientDetail,
  getItemStatusLabel,
  isCustomByWeightItem,
  resolveItemCurrencyCode,
  getEffectiveItemDisplayType,
  getCertifiedCertificateNumberOrNull,
} from '../../utils/orderItemDisplay';
import { weightUnitShort } from '../../utils/customItemPricing';
import ExportPdfButton from '../ExportPdfButton';
import ExportCsvButton from '../ExportCsvButton';
import OrderItemNotes from '../OrderItemNotes';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';
import SelectField from '../inputs/SelectField';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import { useFeedback } from '../../context/FeedbackContext';
import { toggleClientItemsPageSelection, sumFinalPriceByCurrency, formatTotalsByCurrency } from '../../utils/clientItemsSelection';
import {
  cancelOrderItem,
  cancelOrderItems,
  defaultReturnToInventoryForCancel,
  isOrderItemCancellable,
} from '../../utils/orderItemCancel';
import { buttonStartIcon, iconButtonContent } from '../../utils/buttonLoading';
import BulkCancelOrderItemsDialog from '../orderItems/BulkCancelOrderItemsDialog';

const DEFAULT_STATUS_FILTER = [
  ORDER_STATUSES.APPROVED,
  ORDER_STATUSES.REQUESTED,
  ORDER_STATUSES.READY,
  ORDER_STATUSES.MEMO,
];

function ClientItemsTab({
  clientId,
  active,
  isMobile,
  isManager,
  canEditOrderItemPrice,
  canEditOrderItemWeight,
  canAddCustomItem,
  canMoveToCheck,
  canTransferItemsToClient,
  canManageOrders,
  canChangeStatus,
  onItemsTotalChange,
  onOpenMoveToCheck,
  onOpenMoveToClient,
  refreshOpenOrdersCount,
  fetchClientChecks,
  refreshClient,
  selectionClearSignal,
  itemsRefreshRef,
}) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useFeedback();
  const { run: runItemAction, isPending } = useAsyncAction();

  const [clientItems, setClientItems] = useState([]);
  const [clientItemsLoading, setClientItemsLoading] = useState(false);
  const [clientItemsPage, setClientItemsPage] = useState(0);
  const [clientItemsRowsPerPage, setClientItemsRowsPerPage] = useState(25);
  const [clientItemsTotal, setClientItemsTotal] = useState(0);
  const [itemStatusFilter, setItemStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [itemDateFrom, setItemDateFrom] = useState('');
  const [itemDateTo, setItemDateTo] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
  const [addCustomDialogOpen, setAddCustomDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelItems, setCancelItems] = useState([]);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [itemEdits, setItemEdits] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);
  const [itemAuditOpen, setItemAuditOpen] = useState(false);
  const [itemAuditLogs, setItemAuditLogs] = useState([]);
  const [itemAuditLoading, setItemAuditLoading] = useState(false);
  const [itemAuditRef, setItemAuditRef] = useState('');

  useEffect(() => {
    setSelectedItemIds(new Set());
  }, [selectionClearSignal]);

  const fetchClientItems = useCallback(async (cId, page = 0, status = [], dateFrom = '', dateTo = '', limitOverride) => {
    setClientItemsLoading(true);
    try {
      const limit = limitOverride != null ? limitOverride : clientItemsRowsPerPage;
      const params = { clientUserId: cId, page: page + 1, limit, noCheck: true };
      const statusArr = Array.isArray(status) ? status : (status ? [status] : []);
      if (statusArr.length > 0) params.status = statusArr.join(',');
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await axios.get('/api/order-items', { params });
      setClientItems(res.data.orderItems || []);
      const total = res.data.pagination?.total || 0;
      setClientItemsTotal(total);
      onItemsTotalChange(total);
    } catch (error) {
      console.error('Failed to fetch client items:', error);
    } finally {
      setClientItemsLoading(false);
    }
  }, [clientItemsRowsPerPage, onItemsTotalChange]);

  useEffect(() => {
    if (!active || !clientId) return;
    let cancelled = false;
    (async () => {
      let limit = 25;
      try {
        const { data } = await axios.get('/api/users/preferences');
        limit = pickRowsPerPageFromPrefs(data || {}, TABLE_ROWS_PREF_KEYS.clientDetailItems, [10, 25, 50, 100], 25);
      } catch (_) {
        /* keep 25 */
      }
      if (cancelled) return;
      setClientItemsRowsPerPage(limit);
      await fetchClientItems(clientId, 0, DEFAULT_STATUS_FILTER, '', '', limit);
      await refreshClient();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, active, refreshClient]);

  const toggleItemSelect = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedItemIds((prev) => toggleClientItemsPageSelection(clientItems, prev));
  };

  const allPageSelected = clientItems.length > 0 && clientItems.every((i) => selectedItemIds.has(i._id));
  const somePageSelected = selectedItemIds.size > 0 && !allPageSelected;

  const selectedItems = useMemo(
    () => clientItems.filter((i) => selectedItemIds.has(i._id)),
    [clientItems, selectedItemIds],
  );

  const itemsForPdfExport = selectedItemIds.size > 0 ? selectedItems : clientItems;

  const selectedSubtotalByCurrency = useMemo(
    () => sumFinalPriceByCurrency(selectedItems),
    [selectedItems],
  );
  const selectedSubtotalLabel = useMemo(
    () => formatTotalsByCurrency(selectedSubtotalByCurrency, formatCurrency),
    [selectedSubtotalByCurrency],
  );

  const refreshItems = useCallback(async () => {
    if (!clientId) return;
    await fetchClientItems(clientId, clientItemsPage, itemStatusFilter, itemDateFrom, itemDateTo);
    await refreshClient();
  }, [clientId, clientItemsPage, itemStatusFilter, itemDateFrom, itemDateTo, fetchClientItems, refreshClient]);

  useEffect(() => {
    if (itemsRefreshRef) itemsRefreshRef.current = refreshItems;
  }, [itemsRefreshRef, refreshItems]);

  const handleItemApprove = async (groupId, itemId) => {
    await runItemAction(async () => {
      try {
        await axios.post(`/api/orders/${groupId}/items/${itemId}/approve`);
        refreshItems();
        refreshOpenOrdersCount();
      } catch (error) {
        showError(error.response?.data?.error || 'Failed to approve item');
      }
    }, `approve-${itemId}`);
  };

  const handleItemReady = async (groupId, itemId) => {
    await runItemAction(async () => {
      try {
        await axios.post(`/api/orders/${groupId}/items/${itemId}/ready`);
        refreshItems();
        refreshOpenOrdersCount();
      } catch (error) {
        showError(error.response?.data?.error || 'Failed to mark item ready');
      }
    }, `ready-${itemId}`);
  };

  const handleItemToDelivered = async (item) => {
    const groupId = getOrderId(item);
    await runItemAction(async () => {
      try {
        if (groupId) {
          await axios.patch(`/api/orders/${groupId}/items/${item._id}/status`, {
            status: ORDER_STATUSES.MEMO,
          });
        } else {
          await axios.patch(`/api/order-items/${item._id}/status`, {
            status: ORDER_STATUSES.MEMO,
          });
        }
        await refreshItems();
        refreshOpenOrdersCount();
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToMarkDelivered'));
      }
    }, `deliver-${item._id}`);
  };

  const handleItemUndoDelivered = async (item) => {
    const groupId = getOrderId(item);
    await runItemAction(async () => {
      try {
        if (groupId) {
          await axios.patch(`/api/orders/${groupId}/items/${item._id}/status`, {
            status: ORDER_STATUSES.READY,
          });
        } else {
          await axios.patch(`/api/order-items/${item._id}/status`, {
            status: ORDER_STATUSES.READY,
          });
        }
        await refreshItems();
        refreshOpenOrdersCount();
      } catch (error) {
        showError(error.response?.data?.error || t('orders.undoDeliveredFailed'));
      }
    }, `undo-deliver-${item._id}`);
  };

  const canMarkDelivered = (item) =>
    canChangeStatus
    && (item.status === ORDER_STATUSES.APPROVED || item.status === ORDER_STATUSES.READY);

  const canUndoDelivered = (item) =>
    canChangeStatus && item.status === ORDER_STATUSES.MEMO;

  const canCancelClientItem = (item) => {
    if (!canManageOrders || !isOrderItemCancellable(item)) return false;
    return [
      ORDER_STATUSES.REQUESTED,
      ORDER_STATUSES.APPROVED,
      ORDER_STATUSES.READY,
      ORDER_STATUSES.MEMO,
    ].includes(item.status);
  };

  const canSelectClientItems = canMoveToCheck || canTransferItemsToClient || canManageOrders;

  const selectedCancellableItems = selectedItems.filter((item) => canCancelClientItem(item));

  const openBulkCancelSelected = () => {
    const items = selectedCancellableItems.map((item) => ({
      ...item,
      returnToInventory: defaultReturnToInventoryForCancel(item),
    }));
    if (!items.length) return;
    setCancelTarget('bulk');
    setCancelItems(items);
    setCancelDialogOpen(true);
  };

  const openCancelDialog = (item) => {
    setCancelTarget(item);
    setCancelItems([{ ...item, returnToInventory: defaultReturnToInventoryForCancel(item) }]);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelTarget(null);
    setCancelItems([]);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      if (cancelTarget === 'bulk') {
        await cancelOrderItems(cancelItems);
        setSelectedItemIds(new Set());
        showSuccess(t('checks.cancelSelectedSuccess', { count: cancelItems.length }));
      } else {
        const returnToInventory = cancelItems[0]?.returnToInventory ?? false;
        await cancelOrderItem(cancelTarget, { returnToInventory });
      }
      refreshItems();
      refreshOpenOrdersCount();
    } catch (error) {
      showError(error.response?.data?.error || t('orders.failedToCancelItem'));
    } finally {
      setCancelLoading(false);
      closeCancelDialog();
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    );
  };

  const getSortValue = (item, field) => {
    switch (field) {
      case 'ref': return getItemRef(item) || '';
      case 'weight': return getItemCarat(item) ?? -1;
      case 'pricePerCt': return item.pricePerUnit ?? -1;
      case 'price': return item.finalPrice ?? -1;
      case 'date': return item.createdAt ? new Date(item.createdAt).getTime() : 0;
      default: return 0;
    }
  };

  const sortedItems = sortConfig.field
    ? [...clientItems].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.field);
      const bVal = getSortValue(b, sortConfig.field);
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : aVal - bVal;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    })
    : clientItems;

  const setItemEdit = (itemId, field, value) => {
    setItemEdits((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [field]: value } }));
  };

  const handleSaveItemEdit = async (item) => {
    const edits = itemEdits[item._id];
    if (!edits || (edits.pricePerUnit === undefined && edits.weight === undefined)) return;
    setSavingItemId(item._id);
    try {
      const payload = {};
      if (edits.pricePerUnit !== undefined) payload.pricePerUnit = Number(edits.pricePerUnit);
      if ((item.type === DIAMOND_TYPES.NON_CERTIFIED || isCustomByWeightItem(item)) && edits.weight !== undefined) {
        payload.weight = Number(edits.weight);
      }
      const groupId = getOrderId(item);
      if (groupId) {
        await axios.patch(`/api/orders/${groupId}/items/${item._id}`, payload);
      } else {
        await axios.patch(`/api/order-items/${item._id}/pricing`, payload);
      }
      setClientItems((prev) => prev.map((i) => {
        if (i._id !== item._id) return i;
        const updated = { ...i };
        if (payload.pricePerUnit !== undefined) updated.pricePerUnit = payload.pricePerUnit;
        if (payload.weight !== undefined) updated.weight = payload.weight;
        const ppu = payload.pricePerUnit !== undefined ? payload.pricePerUnit : (updated.pricePerUnit ?? null);
        const ct = payload.weight !== undefined ? payload.weight : getItemCarat(updated);
        if (ppu != null && ct != null && ct > 0) {
          updated.finalPrice = Math.round(ppu * ct * 100) / 100;
        }
        return updated;
      }));
      setItemEdits((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to save');
    } finally {
      setSavingItemId(null);
    }
  };

  const openItemAuditLog = async (item) => {
    setItemAuditRef(getItemRef(item));
    setItemAuditOpen(true);
    setItemAuditLogs([]);
    setItemAuditLoading(true);
    try {
      const res = await axios.get('/api/audit-logs', { params: { orderItem: item._id, limit: 100 } });
      setItemAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch item audit logs:', error);
    } finally {
      setItemAuditLoading(false);
    }
  };

  const closeItemAuditLog = () => {
    setItemAuditOpen(false);
    setItemAuditRef('');
  };

  const buildExportConfig = () => {
    const columns = [
      t('orderItems.columns.certNumber'),
      t('orderItems.columns.description'),
      t('orderItems.columns.weight'),
      'Price/ct',
      t('common.price'),
      t('common.date'),
    ];
    const sortedItems = sortOrderItemsByCreatedAt(itemsForPdfExport);
    const rows = sortedItems.map((item) => {
      const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
      const pricePerCt = getItemPricePerCt(item);
      return [
        getItemExportTypeLabel(item, t),
        getOrderItemDescriptionCell(item),
        getItemDetails(item),
        pricePerCt === '—' ? '—' : `${pricePerCt}/${weightSuffix}`,
        getItemDisplayPrice(item),
        item.createdAt ? formatDateTime(item.createdAt) : '—',
      ];
    });
    const totalWeight = sortedItems.reduce((sum, item) => sum + (getItemCarat(item) || 0), 0);
    const totalPriceCell = formatTotalsByCurrency(
      sumFinalPriceByCurrency(sortedItems),
      formatCurrency,
    );
    const totalRow = ['', 'TOTAL', `${totalWeight.toFixed(2)} ct`, '', totalPriceCell, ''];
    const exportingSelection = selectedItemIds.size > 0;
    return {
      title: exportingSelection
        ? `Client Items (${sortedItems.length} selected)`
        : 'Client Items',
      filename: exportingSelection
        ? `client-items-selected-${clientId}.pdf`
        : `client-items-${clientId}.pdf`,
      columns,
      rows,
      totalRow,
    };
  };

  if (!active) return null;

  return (
    <>
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'}>
          <SelectField
            label="Status"
            multiple
            value={itemStatusFilter}
            onChange={(next) => {
              setItemStatusFilter(next);
              setClientItemsPage(0);
              setSelectedItemIds(new Set());
              fetchClientItems(clientId, 0, next, itemDateFrom, itemDateTo);
            }}
            options={ALL_ORDER_STATUSES.map((s) => ({ value: s, label: getItemStatusLabel(s, t) }))}
            fullWidth={isMobile}
            sx={isMobile ? {} : { minWidth: 180 }}
          />
          <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
            <TextField
              size="small" label="From" type="date" value={itemDateFrom} fullWidth
              onChange={(e) => {
                setItemDateFrom(e.target.value);
                setClientItemsPage(0);
                setSelectedItemIds(new Set());
                fetchClientItems(clientId, 0, itemStatusFilter, e.target.value, itemDateTo);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" label="To" type="date" value={itemDateTo} fullWidth
              onChange={(e) => {
                setItemDateTo(e.target.value);
                setClientItemsPage(0);
                setSelectedItemIds(new Set());
                fetchClientItems(clientId, 0, itemStatusFilter, itemDateFrom, e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          {canAddCustomItem && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddCustomDialogOpen(true)}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {t('clients.addItem')}
            </Button>
          )}
        </Stack>
      </Paper>

      {selectedItemIds.size > 0 && canSelectClientItems && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'primary.50' }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body2" fontWeight="bold">
              {t('clients.selectedCountSubtotal', { count: selectedItemIds.size, subtotal: selectedSubtotalLabel })}
            </Typography>
            {canManageOrders && selectedCancellableItems.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={buttonStartIcon(cancelLoading, CancelIcon)}
                onClick={openBulkCancelSelected}
                disabled={cancelLoading}
              >
                {t('checks.cancelSelectedItems', { count: selectedCancellableItems.length })}
              </Button>
            )}
            {canMoveToCheck && (
              <Button size="small" variant="outlined" startIcon={<CheckIcon />}
                onClick={() => { fetchClientChecks(clientId); onOpenMoveToCheck(Array.from(selectedItemIds)); }}>
                {t('clients.moveToCheck')}
              </Button>
            )}
            {canTransferItemsToClient && (
              <Button size="small" variant="outlined" startIcon={<TransferIcon />}
                onClick={() => onOpenMoveToClient(Array.from(selectedItemIds))}>
                {t('clients.moveItemsToClient')}
              </Button>
            )}
            <ExportPdfButton
              exportConfig={buildExportConfig()}
              label={t('export.pdfSelected', { count: selectedItemIds.size })}
            />
            <ExportCsvButton
              exportConfig={buildExportConfig()}
              label={t('export.csvSelected', { count: selectedItemIds.size })}
            />
          </Box>
        </Paper>
      )}

      {clientItemsLoading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
      ) : (
        <>
          {clientItems.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>{t('clients.noItems')}</Typography>
          ) : isMobile ? (
            <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
              {canSelectClientItems && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={allPageSelected}
                      indeterminate={somePageSelected}
                      onChange={toggleSelectAllPage}
                    />
                  )}
                  label={t('clients.selectAllOnPage')}
                />
              )}
              <Stack direction="row" spacing={1} sx={{ ml: canSelectClientItems ? 'auto' : undefined }}>
                <ExportPdfButton
                  exportConfig={buildExportConfig()}
                  disabled={itemsForPdfExport.length === 0}
                />
                <ExportCsvButton
                  exportConfig={buildExportConfig()}
                  disabled={itemsForPdfExport.length === 0}
                />
              </Stack>
            </Box>
            <Stack spacing={1}>
              {clientItems.map((item) => {
                const edits = itemEdits[item._id];
                const isNonCert = item.type === DIAMOND_TYPES.NON_CERTIFIED;
                const showPricePerCtInput = canEditOrderItemPrice;
                const showWeightInput = canEditOrderItemWeight && (isNonCert || isCustomByWeightItem(item));
                const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
                const isSelected = selectedItemIds.has(item._id);
                return (
                  <Card
                    key={item._id}
                    variant="outlined"
                    sx={{ borderColor: isSelected ? 'primary.main' : 'divider', bgcolor: isSelected ? 'primary.50' : 'background.paper' }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                        {canSelectClientItems && (
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => toggleItemSelect(item._id)}
                            sx={{ p: 0 }}
                            inputProps={{ 'aria-label': t('checks.selectItem') }}
                          />
                        )}
                        <Chip label={getItemTypeLabel(item, t)} size="small" variant="outlined" />
                        <Chip label={getItemStatusLabel(item.status, t)} color={getItemStatusColorClientDetail(item.status)} size="small" />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
                          {item.createdAt ? formatDateTime(item.createdAt) : '—'}
                        </Typography>
                        {(canManageOrders || canChangeStatus) && (
                          <>
                            {isManager && item.status === ORDER_STATUSES.REQUESTED && (
                              <Tooltip title={t('status.approved')}>
                                <IconButton size="small" color="primary" sx={{ p: 0.25 }} onClick={() => handleItemApprove(getOrderId(item), item._id)} disabled={isPending(`approve-${item._id}`)}>{iconButtonContent(isPending(`approve-${item._id}`), <ApproveIcon fontSize="small" />)}</IconButton>
                              </Tooltip>
                            )}
                            {isManager && item.status === ORDER_STATUSES.APPROVED && (
                              <Tooltip title="Ready">
                                <IconButton size="small" color="primary" sx={{ p: 0.25 }} onClick={() => handleItemReady(getOrderId(item), item._id)} disabled={isPending(`ready-${item._id}`)}>{iconButtonContent(isPending(`ready-${item._id}`), <ReadyIcon fontSize="small" />)}</IconButton>
                              </Tooltip>
                            )}
                            {canMarkDelivered(item) && (
                              <Tooltip title={t('orders.markAsDelivered')}>
                                <IconButton size="small" color="warning" sx={{ p: 0.25 }} onClick={() => handleItemToDelivered(item)} disabled={isPending(`deliver-${item._id}`)}>{iconButtonContent(isPending(`deliver-${item._id}`), <DeliverActionIcon />)}</IconButton>
                              </Tooltip>
                            )}
                            {canUndoDelivered(item) && (
                              <Tooltip title={t('orders.undoDelivered')}>
                                <IconButton size="small" color="primary" sx={{ p: 0.25 }} onClick={() => handleItemUndoDelivered(item)} disabled={isPending(`undo-deliver-${item._id}`)}>{iconButtonContent(isPending(`undo-deliver-${item._id}`), <ReadyIcon fontSize="small" />)}</IconButton>
                              </Tooltip>
                            )}
                            {canCancelClientItem(item) && (
                              <Tooltip title={t('orders.cancelItem')}>
                                <IconButton size="small" color="error" sx={{ p: 0.25 }} onClick={() => openCancelDialog(item)}><CancelIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Audit Log">
                              <IconButton size="small" sx={{ p: 0.25 }} onClick={() => openItemAuditLog(item)}><HistoryIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>

                      <Box mb={0.75}>
                        {getEffectiveItemDisplayType(item) === DIAMOND_TYPES.CERTIFIED ? (
                          <>
                            {getCertifiedCertificateNumberOrNull(item) ? (
                              <CertificateLink
                                certificateNumber={getCertifiedCertificateNumberOrNull(item)}
                                lab={getItemLab(item)}
                                variant="body2"
                                sx={{ fontWeight: 'bold' }}
                              />
                            ) : (
                              <Typography variant="body2" fontWeight="bold">{getItemRef(item)}</Typography>
                            )}
                            {getCertifiedDiamondSpecsWithWeight(item) !== '—' && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {getCertifiedDiamondSpecsWithWeight(item)}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" fontWeight="bold">{getItemRef(item)}</Typography>
                        )}
                      </Box>

                      <Divider sx={{ mb: 0.75 }} />

                      <OrderItemNotes item={item} sx={{ mb: 0.75 }} />

                      <Box display="flex" alignItems="center" gap={1}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {showWeightInput ? (
                            <>
                              <TextField size="small" type="number" inputProps={{ min: 0.01, step: 0.01 }}
                                value={edits?.weight !== undefined ? edits.weight : (item.weight ?? '')}
                                onChange={(e) => setItemEdit(item._id, 'weight', e.target.value)}
                                onBlur={() => edits?.weight !== undefined ? handleSaveItemEdit(item) : null}
                                disabled={savingItemId === item._id}
                                sx={{ width: 65, '& .MuiInputBase-root': { height: 28 }, '& .MuiInputBase-input': { py: '4px', px: '8px' } }} />
                              <Typography variant="caption" color="text.secondary">{weightSuffix}</Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">{getItemDetails(item)}</Typography>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {showPricePerCtInput ? (
                            <>
                              <Typography variant="caption" color="text.secondary">{`${getCurrencySymbol(resolveItemCurrencyCode(item))}/${weightSuffix}`}</Typography>
                              <TextField size="small" type="number" inputProps={{ min: 0, step: 1 }}
                                value={edits?.pricePerUnit !== undefined ? edits.pricePerUnit : (item.pricePerUnit != null ? item.pricePerUnit : (getItemCarat(item) > 0 && item.finalPrice != null ? item.finalPrice / getItemCarat(item) : ''))}
                                onChange={(e) => setItemEdit(item._id, 'pricePerUnit', e.target.value)}
                                onBlur={() => edits?.pricePerUnit !== undefined ? handleSaveItemEdit(item) : null}
                                disabled={savingItemId === item._id}
                                sx={{ width: 85, '& .MuiInputBase-root': { height: 28 }, '& .MuiInputBase-input': { py: '4px', px: '8px' } }} />
                              {savingItemId === item._id && <CircularProgress size={12} sx={{ ml: 0.5, flexShrink: 0 }} />}
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">{`${getItemPricePerCt(item)}/${weightSuffix}`}</Typography>
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ ml: 'auto' }}>
                          {getItemDisplayPrice(item, edits)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
            </>
          ) : (
            <>
              <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
                <ExportPdfButton
                  exportConfig={buildExportConfig()}
                  disabled={itemsForPdfExport.length === 0}
                />
                <ExportCsvButton
                  exportConfig={buildExportConfig()}
                  disabled={itemsForPdfExport.length === 0}
                />
              </Box>
              <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {canSelectClientItems && <TableCell padding="checkbox"><Checkbox size="small" checked={allPageSelected} indeterminate={somePageSelected} onChange={toggleSelectAllPage} inputProps={{ 'aria-label': t('clients.selectAllOnPage') }} /></TableCell>}
                    <TableCell>{t('common.type')}</TableCell>
                    <TableCell sortDirection={sortConfig.field === 'ref' ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === 'ref'}
                        direction={sortConfig.field === 'ref' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('ref')}
                      >{t('orderItems.columns.description')}</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortConfig.field === 'weight' ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === 'weight'}
                        direction={sortConfig.field === 'weight' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('weight')}
                      >{t('orderItems.columns.weight')}</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortConfig.field === 'pricePerCt' ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === 'pricePerCt'}
                        direction={sortConfig.field === 'pricePerCt' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('pricePerCt')}
                      >Price/ct</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortConfig.field === 'price' ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === 'price'}
                        direction={sortConfig.field === 'price' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('price')}
                      >{t('common.price')}</TableSortLabel>
                    </TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell sortDirection={sortConfig.field === 'date' ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === 'date'}
                        direction={sortConfig.field === 'date' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('date')}
                      >{t('common.date')}</TableSortLabel>
                    </TableCell>
                    <TableCell>Order</TableCell>
                    {(isManager || canChangeStatus) && <TableCell align="right">{t('common.actions')}</TableCell>}
                    {canMoveToCheck && !isManager && !canChangeStatus && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedItems.map((item) => {
                    const edits = itemEdits[item._id];
                    const isNonCert = item.type === DIAMOND_TYPES.NON_CERTIFIED;
                    const showPricePerCtInput = canEditOrderItemPrice;
                    const showWeightInput = canEditOrderItemWeight && (isNonCert || isCustomByWeightItem(item));
                    const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
                    const isSelected = selectedItemIds.has(item._id);
                    return (
                      <TableRow key={item._id} hover selected={isSelected}>
                        {canSelectClientItems && <TableCell padding="checkbox"><Checkbox size="small" checked={isSelected} onChange={() => toggleItemSelect(item._id)} inputProps={{ 'aria-label': t('checks.selectItem') }} /></TableCell>}
                        <TableCell><Chip label={getItemTypeLabel(item, t)} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          {getEffectiveItemDisplayType(item) === DIAMOND_TYPES.CERTIFIED ? (
                            <Box>
                              {getCertifiedCertificateNumberOrNull(item) ? (
                                <CertificateLink
                                  certificateNumber={getCertifiedCertificateNumberOrNull(item)}
                                  lab={getItemLab(item)}
                                  variant="body2"
                                />
                              ) : (
                                <Typography variant="body2" fontWeight="bold">{getItemRef(item)}</Typography>
                              )}
                              {getCertifiedDiamondSpecsWithWeight(item) !== '—' && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {getCertifiedDiamondSpecsWithWeight(item)}
                                </Typography>
                              )}
                              <OrderItemNotes item={item} />
                            </Box>
                          ) : (
                            <>
                              {getItemRef(item)}
                              <OrderItemNotes item={item} />
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          {showWeightInput ? (
                            <Box display="inline-flex" alignItems="center" gap={0.5}>
                              <TextField size="small" type="number" inputProps={{ min: 0.01, step: 0.01 }}
                                value={edits?.weight !== undefined ? edits.weight : (item.weight ?? '')}
                                onChange={(e) => setItemEdit(item._id, 'weight', e.target.value)}
                                onBlur={() => edits?.weight !== undefined ? handleSaveItemEdit(item) : null}
                                disabled={savingItemId === item._id}
                                sx={{ width: 70, '& .MuiInputBase-root': { height: 28 }, '& .MuiInputBase-input': { py: '4px', px: '8px' } }} />
                              <span>{weightSuffix}</span>
                            </Box>
                          ) : getItemDetails(item)}
                        </TableCell>
                        <TableCell>
                          {showPricePerCtInput ? (
                            <Box display="inline-flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" color="text.secondary">{getCurrencySymbol(resolveItemCurrencyCode(item))}</Typography>
                              <TextField size="small" type="number" inputProps={{ min: 0, step: 1 }}
                                value={edits?.pricePerUnit !== undefined ? edits.pricePerUnit : (item.pricePerUnit != null ? item.pricePerUnit : (getItemCarat(item) > 0 && item.finalPrice != null ? item.finalPrice / getItemCarat(item) : ''))}
                                onChange={(e) => setItemEdit(item._id, 'pricePerUnit', e.target.value)}
                                onBlur={() => edits?.pricePerUnit !== undefined ? handleSaveItemEdit(item) : null}
                                disabled={savingItemId === item._id}
                                sx={{ width: 90, '& .MuiInputBase-root': { height: 28 }, '& .MuiInputBase-input': { py: '4px', px: '8px' } }} />
                              {savingItemId === item._id && <CircularProgress size={12} sx={{ ml: 0.5, flexShrink: 0 }} />}
                            </Box>
                          ) : (getItemPricePerCt(item) === '—' ? '—' : `${getItemPricePerCt(item)}/${weightSuffix}`)}
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight="bold">{getItemDisplayPrice(item, edits)}</Typography></TableCell>
                        <TableCell><Chip label={getItemStatusLabel(item.status, t)} color={getItemStatusColorClientDetail(item.status)} size="small" /></TableCell>
                        <TableCell><Typography variant="caption">{item.createdAt ? formatDateTime(item.createdAt) : '—'}</Typography></TableCell>
                        <TableCell>
                          {getOrderId(item) ? (
                            <RouterLink
                              to={getClientTabPath(clientId, 'orders', getOrderId(item))}
                              style={{ fontFamily: 'monospace', fontSize: '0.75rem', textDecoration: 'none', color: 'inherit', fontWeight: 600 }}
                              onClick={(e) => e.stopPropagation()}>
                              #{String(getOrderId(item)).slice(-6).toUpperCase()}
                            </RouterLink>
                          ) : '—'}
                        </TableCell>
                        {(canManageOrders || canChangeStatus) && (
                          <TableCell align="right">
                            <Box display="inline-flex" gap={0.5}>
                              {item.status === ORDER_STATUSES.REQUESTED && (
                                <>
                                  {isManager && <Tooltip title="Approve"><IconButton size="small" color="primary" onClick={() => handleItemApprove(getOrderId(item), item._id)} disabled={isPending(`approve-${item._id}`)}>{iconButtonContent(isPending(`approve-${item._id}`), <ApproveIcon fontSize="small" />)}</IconButton></Tooltip>}
                                  {canManageOrders && <Tooltip title={t('orders.cancelItem')}><IconButton size="small" color="error" onClick={() => openCancelDialog(item)}><CancelIcon fontSize="small" /></IconButton></Tooltip>}
                                </>
                              )}
                              {item.status === ORDER_STATUSES.APPROVED && (
                                <>
                                  {isManager && <Tooltip title="Ready"><IconButton size="small" color="primary" onClick={() => handleItemReady(getOrderId(item), item._id)} disabled={isPending(`ready-${item._id}`)}>{iconButtonContent(isPending(`ready-${item._id}`), <ReadyIcon fontSize="small" />)}</IconButton></Tooltip>}
                                  {canMarkDelivered(item) && (
                                    <Tooltip title={t('orders.markAsDelivered')}>
                                      <IconButton size="small" color="warning" onClick={() => handleItemToDelivered(item)} disabled={isPending(`deliver-${item._id}`)}>{iconButtonContent(isPending(`deliver-${item._id}`), <DeliverActionIcon />)}</IconButton>
                                    </Tooltip>
                                  )}
                                  {canManageOrders && <Tooltip title={t('orders.cancelItem')}><IconButton size="small" color="error" onClick={() => openCancelDialog(item)}><CancelIcon fontSize="small" /></IconButton></Tooltip>}
                                </>
                              )}
                              {item.status === ORDER_STATUSES.READY && (
                                <>
                                  {canMarkDelivered(item) && (
                                    <Tooltip title={t('orders.markAsDelivered')}>
                                      <IconButton size="small" color="warning" onClick={() => handleItemToDelivered(item)} disabled={isPending(`deliver-${item._id}`)}>{iconButtonContent(isPending(`deliver-${item._id}`), <DeliverActionIcon />)}</IconButton>
                                    </Tooltip>
                                  )}
                                  {canManageOrders && <Tooltip title={t('orders.cancelItem')}><IconButton size="small" color="error" onClick={() => openCancelDialog(item)}><CancelIcon fontSize="small" /></IconButton></Tooltip>}
                                </>
                              )}
                              {item.status === ORDER_STATUSES.MEMO && (
                                <>
                                  {canUndoDelivered(item) && (
                                    <Tooltip title={t('orders.undoDelivered')}>
                                      <IconButton size="small" color="primary" onClick={() => handleItemUndoDelivered(item)} disabled={isPending(`undo-deliver-${item._id}`)}>{iconButtonContent(isPending(`undo-deliver-${item._id}`), <ReadyIcon fontSize="small" />)}</IconButton>
                                    </Tooltip>
                                  )}
                                  {canManageOrders && <Tooltip title={t('orders.cancelItem')}><IconButton size="small" color="error" onClick={() => openCancelDialog(item)}><CancelIcon fontSize="small" /></IconButton></Tooltip>}
                                </>
                              )}
                              <Tooltip title="Audit Log"><IconButton size="small" onClick={() => openItemAuditLog(item)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
          <TablePagination
            component="div"
            count={clientItemsTotal}
            page={clientItemsPage}
            rowsPerPage={clientItemsRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(e, newPage) => {
              setClientItemsPage(newPage);
              setSelectedItemIds(new Set());
              fetchClientItems(clientId, newPage, itemStatusFilter, itemDateFrom, itemDateTo);
            }}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setClientItemsRowsPerPage(next);
              setClientItemsPage(0);
              setSelectedItemIds(new Set());
              saveTableRowsPreference(TABLE_ROWS_PREF_KEYS.clientDetailItems, next).catch(() => {});
              fetchClientItems(clientId, 0, itemStatusFilter, itemDateFrom, itemDateTo, next);
            }}
          />
        </>
      )}

      <BulkCancelOrderItemsDialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        items={cancelItems}
        onItemsChange={setCancelItems}
        onConfirm={confirmCancel}
        loading={cancelLoading}
        isMobile={isMobile}
        t={t}
        confirmKey={cancelTarget === 'bulk' ? 'checks.cancelSelectedItems' : 'orders.cancelItem'}
      />

      <AddCustomItemDialog
        open={addCustomDialogOpen}
        onClose={() => setAddCustomDialogOpen(false)}
        clientId={clientId}
        fullScreen={isMobile}
        onSuccess={() => {
          fetchClientItems(clientId, 0, itemStatusFilter, itemDateFrom, itemDateTo);
          setClientItemsPage(0);
        }}
      />

      <ItemAuditLogDialog
        open={itemAuditOpen}
        onClose={closeItemAuditLog}
        loading={itemAuditLoading}
        logs={itemAuditLogs}
        itemRef={itemAuditRef}
        isMobile={isMobile}
        getAuditStatusLabel={getAuditStatusLabelClientDetail}
        getItemStatusColor={getItemStatusColorClientDetail}
        title="Item Audit Log"
        closeLabel="Close"
        emptyMessage="No audit logs found for this item."
        loadingMode="spinner"
        mobileCardLayout
        tableHeaderMode="plain"
      />
    </>
  );
}

export default ClientItemsTab;
